import { getCurrentSession } from 'remote/auth';
import { Cognito } from 'utils/cognito';
import { hasAcceptedCookies, isRecentCookieTimestamp } from 'utils/cookies';
import { init as initDataDogLogs } from 'utils/datadog';
import { RBIEnv, brand, env, isLocalDev, platform } from 'utils/environment';
import LaunchDarklyHelper, { LaunchDarklyFlag } from 'utils/launchdarkly';
import logger from 'utils/logger';
import LogRocketHelper from 'utils/logrocket';

interface GtmEvent {
  event: string;
  brand: string;
  timestamp: number;
  duration: number;
  platform: string;
}

declare global {
  interface Window {
    dataLayer: GtmEvent[];
    LOADING_START_TIME: number;
    /**
     * window.Cypress exists when our app is running within Cypress
     * Places we currently check for this:
     * - frontend/src/state/graphql/links/index.ts - avoid BatchHttpLink
     *
     * https://docs.cypress.io/faq/questions/using-cypress-faq.html#Is-there-any-way-to-detect-if-my-app-is-running-under-Cypress
     */
    Cypress?: any;
    // When run with cypress for cypress-v2 package,
    // we instantiate the LD client with predefined flags
    _initial_cypress_feature_flags?: object;
    // Skip the interval for checking for unavailable items.
    // Avoids race condition in cart with recorded tests
    _skipUnavailableItemsIntervalCheck?: boolean;
  }
}

const initLaunchDarkly = async (bootstrapFlags?: object): Promise<Record<string, unknown>> => {
  const ldClient = await LaunchDarklyHelper.init(bootstrapFlags);

  // NOTE: cypress-v2 test suite requirement
  // Avoid launchDarkly flag stream updates via window.EventSource.
  if (window.Cypress) {
    ldClient.setStreaming(false);
  }

  return new Promise<Record<string, unknown>>((resolve, reject) => {
    ldClient.on('initialized', () => {
      const allFlags = ldClient.allFlags();
      const flattenedFlags = LaunchDarklyHelper.flattenFlags(allFlags);
      const ddSampleRate = flattenedFlags[LaunchDarklyFlag.DATADOG_LOG_SAMPLE_RATE];
      const logRocketFlag = flattenedFlags[LaunchDarklyFlag.ENABLE_LOGROCKET];
      const cookieBannerFlag = flattenedFlags[LaunchDarklyFlag.ENABLE_COOKIE_BANNER];
      const isCookieVersioningEnabled = flattenedFlags[LaunchDarklyFlag.ENABLE_COOKIE_VERSIONING];
      const cookiesAccepted = isCookieVersioningEnabled
        ? hasAcceptedCookies()
        : isRecentCookieTimestamp();

      initDataDogLogs(ddSampleRate);

      if (logRocketFlag && (!cookieBannerFlag || cookiesAccepted)) {
        LogRocketHelper.init();
      }

      resolve(flattenedFlags);
    });

    ldClient.on('failed', () => reject(new Error('LD Init failed')));
    ldClient.on('error', error => reject(error));
  }).catch(error => {
    logger.error(error);
    return {};
  });
};

const initCognitoSession = async (): Promise<void> => {
  Cognito.configure();
  await getCurrentSession();
};

const emitLoadingSuccessEvent = (rbiBrand: string, rbiPlatform: string) => {
  const successTime = performance.now?.();
  const durationTillSuccess = successTime - window.LOADING_START_TIME;
  (window.dataLayer || []).push({
    event: 'APP_BUNDLE_LOADING_SUCCESS',
    brand: rbiBrand,
    timestamp: successTime,
    duration: durationTillSuccess,
    platform: rbiPlatform
  });
};

export default async function bootstrap(
  renderApp: (flags: Record<string, unknown>) => void
): Promise<void> {
  const [ENV, BRAND, PLATFORM] = [env(), brand().toUpperCase(), platform()];

  if (!isLocalDev) {
    emitLoadingSuccessEvent(BRAND, PLATFORM);
  }

  if (ENV === RBIEnv.TEST) {
    const defaultFlags = {};
    await initCognitoSession();
    renderApp(defaultFlags);
    return;
  }

  let bootstrapFlags: object | undefined;
  // NOTE: cypress-v2 test suite requirement
  // Allow us to set initial values for launchDarkly.
  if (window.Cypress) {
    bootstrapFlags = window._initial_cypress_feature_flags;
  }

  const flags = await initLaunchDarkly(bootstrapFlags);
  await initCognitoSession();

  renderApp(flags);
}
